import React, { useState, useEffect, useMemo } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AuthService from '../services/auth';

export default function AppTour() {
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const role = AuthService.getCurrentUserRole();

  const locale = useMemo(() => ({
    back: 'Atrás',
    close: 'Cerrar',
    last: 'Finalizar',
    next: 'Siguiente',
    nextLabelWithProgress: 'Siguiente ({step} de {steps})',
    open: 'Abrir',
    skip: 'Omitir',
    stepXofY: '{{current}} de {{count}}',
  }), []);

  useEffect(() => {
    const getSteps = () => {
      let roleSteps = [];
      const getTarget = (desktopTarget, mobileTarget) => {
        return isDesktop ? desktopTarget : (mobileTarget || desktopTarget);
      };

      if (role === 'habitante') {
        roleSteps = [
          {
            target: 'body',
            content: '¡Bienvenido a Migarita Online! Vamos a darte un recorrido rápido por las funciones principales.',
            placement: 'center',
            disableBeacon: true,
          },
          {
            target: getTarget('#nav-visitas', '#bottom-nav-visitas'),
            content: 'Aquí puedes crear y compartir códigos QR o registrar visitas regulares para tus invitados.',
          },
          {
            target: getTarget('#nav-amenidades', '#bottom-nav-amenidades'),
            content: 'Explora y reserva las amenidades disponibles en tu condominio desde aquí.',
          },
          {
            target: getTarget('#nav-reportes', '#bottom-nav-menu'),
            content: isDesktop ? 'Si encuentras algún problema o avería en el edificio, repórtalo rápidamente aquí.' : 'Abre el menú y selecciona "Reportar Avería" si encuentras algún problema o avería.',
          },
          {
            target: getTarget('#quick-actions', '#quick-actions'),
            content: 'Las Acciones Rápidas son reorganizables: toca el icono de editar y arrastra los botones para personalizar tu acceso directo favorito.',
          }
        ];
      } else if (role === 'receptor') {
        roleSteps = [
          {
            target: 'body',
            content: '¡Bienvenido a Recepción! Vamos a repasar tus herramientas principales.',
            placement: 'center',
            disableBeacon: true,
          },
          {
            target: getTarget('#nav-recepcion', '#bottom-nav-inicio'),
            content: 'Este es el Hub de Recepción, donde podrás ver las visitas programadas del día y escanear códigos.',
          },
          {
            target: getTarget('#nav-visitas', '#bottom-nav-visitas'),
            content: 'Utiliza esta sección para registrar visitas manualmente a las unidades.',
          }
        ];
      } else if (role === 'admin' || role === 'superuser') {
        roleSteps = [
          {
            target: 'body',
            content: '¡Bienvenido Administrador! Conoce tus atajos para gestionar la comunidad.',
            placement: 'center',
            disableBeacon: true,
          },
          {
            target: getTarget('#nav-useradmin', '#bottom-nav-menu'),
            content: isDesktop ? 'Aquí podrás administrar los roles de usuarios y dar de alta a nuevos inquilinos o dueños.' : 'En el menú encontrarás "Admin. de Usuarios" para dar de alta inquilinos o dueños.',
          },
          {
            target: getTarget('#nav-admin', '#bottom-nav-inicio'),
            content: 'En tu panel de control podrás gestionar métricas y opciones.',
          },
          {
            target: getTarget('#nav-anuncios', '#bottom-nav-menu'),
            content: isDesktop ? 'Usa esta herramienta para enviar comunicados y anuncios a todos los habitantes.' : 'Abre el menú y usa "Anunciar" para enviar comunicados a todos los habitantes.',
          }
        ];
      }
      return roleSteps;
    };

    setSteps(getSteps());
  }, [role, isDesktop]);

  useEffect(() => {
    const handleStartTour = () => {
      // Residents (habitante/receptor) use the OnboardingCarousel instead of
      // the joyride tooltip tour. Skip joyride for those roles so the two
      // systems don't overlap.
      if (role === 'habitante' || role === 'receptor') {
        return;
      }
      // Delay slightly to allow any navigational changes to complete before starting joyride
      setTimeout(() => setRun(true), 500);
    };

    window.addEventListener('mgo:start-tour', handleStartTour);
    return () => window.removeEventListener('mgo:start-tour', handleStartTour);
  }, [role]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRun(false);
    }
  };

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton
      run={run}
      scrollToFirstStep
      showProgress
      showSkipButton
      steps={steps}
      styles={{
        options: {
          zIndex: 10000,
          primaryColor: theme.palette.primary.main,
          borderRadius: 24,
        },
        tooltip: {
          borderRadius: 24,
        },
        tooltipContainer: {
          borderRadius: 24,
        },
      }}
      locale={locale}
    />
  );
}
